/**
 * The drone tablet (WORKBOARD I8; owner: setting a course in first person wants a PDA with a
 * simplified map): the I7 topo map at tablet size with a route drawn on it. The operator stands still
 * while it is open (Match.beginDroneTasking); click to add waypoints, Backspace to take one back,
 * Enter (or the button) to launch, Esc or its own key to put it away (Match.cancelDroneTasking; ui/l0Keys.ts — Q since 68.2b).
 *
 * It takes most of the screen over a dimmed game (owner, Oct 9: "take up more of the screen, be more
 * obtrusive") and opens on the operator with the drone's whole one-way reach in view and ringed; drag
 * pans, the wheel or − / = zooms, 0 goes back to the reach. The corner map draws the same data smaller.
 *
 * The route shows its battery: a drone flies at a fixed speed for a fixed time (PersonSet
 * droneSpeed × droneEndurance), so the route is drawn in the team's flash up to where the battery runs
 * out, marked ✕, and dashed red beyond — the drone ends there. The look is plain military software,
 * Ravenfield / Carrier Command level (I9's note), not greebles.
 *
 * 65.16: an operator carrying strike drones (65.19: or a charge, the explosive payload) gets one more verb — S, then a click: the strike (snapped to a known enemy
 * on the map, else the point); the route still applies, and with no route it flies straight there. Backspace takes the
 * strike back first. Everything else is the scout drone's tablet.
 *
 * Presentation and input only: it reads state and hands the chosen waypoints to the host, which calls
 * the Match actions. The route maths is pure and exported (tests/droneTablet.test.ts).
 */
import { l0KeyIs } from './l0Keys';
import type { GameState, StrikeTarget, Vec3 } from '../types';
import { teamPicture } from '../core/picture';
import { TEAMS } from '../constants';
import { legLengths } from '../core/drones';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { unitPaletteFor } from '../rendering/unitPalette';
import { TopoMap, topoFrame } from './topoMap';

// ---------------------------------------------------------------------------
// Route maths (pure)
// ---------------------------------------------------------------------------

const norm = (a: Vec3): Vec3 => { const l = Math.hypot(a.x, a.y, a.z) || 1; return { x: a.x / l, y: a.y / l, z: a.z / l }; };

/** The arc length of a route (unit directions) on a planet of `radius`. */
export function routeLength(route: readonly Vec3[], radius: number): number {
  return legLengths(route.map(norm), radius).reduce((a, b) => a + b, 0);
}

/** Where along the route a drone with `reach` (arc length) runs out: the leg, the fraction along it, the point. Null if it makes the whole route. */
export function batteryOut(route: readonly Vec3[], radius: number, reach: number): { leg: number; t: number; point: Vec3 } | null {
  const r = route.map(norm);
  const legs = legLengths(r, radius);
  let left = reach;
  for (let i = 0; i < legs.length; i++) {
    if (left < legs[i]) {
      const t = legs[i] > 0 ? left / legs[i] : 0;
      return { leg: i, t, point: slerp(r[i], r[i + 1], t) };
    }
    left -= legs[i];
  }
  return null;
}

function slerp(a: Vec3, b: Vec3, t: number): Vec3 {
  const d = Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z));
  const th = Math.acos(d);
  if (th < 1e-12) return { ...a };
  const s = Math.sin(th), wa = Math.sin((1 - t) * th) / s, wb = Math.sin(t * th) / s;
  return norm({ x: a.x * wa + b.x * wb, y: a.y * wa + b.y * wb, z: a.z * wa + b.z * wb });
}

/** How far a drone flies on one battery, in arc length (its speed × its endurance). */
export function droneReach(state: Parameters<typeof personSetOf>[0]): number {
  const ps = personSetOf(state);
  return ps.droneSpeed * ps.droneEndurance;
}

// ---------------------------------------------------------------------------
// The tablet
// ---------------------------------------------------------------------------

export interface DroneTabletOptions {
  /** Launch with these waypoints (the operator's spot is the route's start), and a strike drone's strike when one is set. */
  onLaunch: (waypoints: Vec3[], strike?: StrikeTarget) => void;
  onCancel: () => void;
  /** Why tasking was refused (no drones, mounted …): the tablet shows it and only closes. */
  blocked?: string;
}

const MAX_WAYPOINTS = 8;
/** 65.16: a click this near a known enemy's marker strikes the unit, not the ground. */
const STRIKE_SNAP_PX = 16;
/** A press that moves less than this many pixels is a click (a waypoint), more is a drag (a pan). */
const DRAG_PX = 5;

/** The tablet's map size for a window: most of the screen (owner: "take up more of the screen, be more obtrusive"). */
export function tabletMapSize(width: number, height: number): number {
  return Math.round(Math.max(360, Math.min(height - 150, width - 80, 1100)));
}

export class DroneTablet {
  readonly el: HTMLDivElement;
  private readonly map: TopoMap;
  private readonly overlay: HTMLCanvasElement;
  private readonly g: CanvasRenderingContext2D;
  private readonly size: number;
  private readonly waypoints: Vec3[] = [];
  private hover: { x: number; y: number } | null = null;
  private press: { x: number; y: number; dragged: boolean } | null = null;
  private readonly status: HTMLElement;
  private readonly launchBtn: HTMLButtonElement;
  private readonly cleanup: Array<() => void> = [];
  private closed = false;
  /** 65.16: the operator carries strike drones; S arms the next click as the strike; the strike set. */
  private readonly striker: boolean;
  private arming = false;
  private strike: { dir: Vec3; unitId?: number } | null = null;
  private tick = 0;

  constructor(private state: GameState, private readonly operatorId: number, private readonly opts: DroneTabletOptions) {
    const op = state.players.find(p => p.id === operatorId)!;
    this.striker = op.droneKind === 'strike' || (op.charges ?? 0) > 0;   // 65.19: or it carries a charge (the explosive payload)
    const planet = state.planets[op.planetIndex];
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    const pal = unitPaletteFor(TEAMS[op.teamIndex % TEAMS.length]);
    const size = this.size = tabletMapSize(window.innerWidth, window.innerHeight);
    // open on the operator with the drone's whole one-way reach in view (pan and zoom from there)
    const frame = topoFrame(op.planetIndex, planet.radius, [op.surfaceDir], Math.max(20 * person, droneReach(state) * 1.08));

    this.el = document.createElement('div');
    this.el.className = 'drone-tablet';
    this.el.innerHTML = `
      <style>
        .drone-tablet { position: fixed; inset: 0; z-index: 60; display: grid; place-items: center; background: rgba(6,9,12,0.66); user-select: none; }
        .drone-tablet .pad { width: ${size + 32}px; padding: 14px 16px 12px; background: #12171c; border: 1px solid #2c3640; border-radius: 16px; box-shadow: 0 12px 60px rgba(0,0,0,0.8);
          font: 500 12px/1.4 system-ui, sans-serif; color: #d8dee6; }
        .drone-tablet .hd { display: flex; justify-content: space-between; align-items: center; gap: 14px; margin-bottom: 8px; border-bottom: 2px solid ${pal.body}; padding-bottom: 6px; }
        .drone-tablet .hd b { font: 700 14px/1 system-ui, sans-serif; letter-spacing: 0.14em; color: #eef2f6; }
        .drone-tablet .hd span { color: #8b97a3; font-variant-numeric: tabular-nums; }
        .drone-tablet .zoom { display: flex; gap: 4px; }
        .drone-tablet .zoom button { background: #222b33; color: #d8dee6; padding: 3px 10px; font-weight: 700; letter-spacing: 0; }
        .drone-tablet .mapwrap { position: relative; width: ${size}px; height: ${size}px; }
        .drone-tablet canvas.route { position: absolute; left: 0; top: 0; cursor: crosshair; }
        .drone-tablet canvas.route.dragging { cursor: grabbing; }
        .drone-tablet .ft { display: flex; justify-content: space-between; align-items: center; gap: 14px; margin-top: 8px; color: #8b97a3; }
        .drone-tablet .hint { color: #8b97a3; font-size: 11px; line-height: 1.5; }
        .drone-tablet .ft .st { font-variant-numeric: tabular-nums; color: #d8dee6; font-size: 13px; }
        .drone-tablet button { background: ${pal.body}; color: #0b0e12; border: 0; border-radius: 6px; padding: 7px 16px; font: 700 12px system-ui, sans-serif; letter-spacing: 0.08em; cursor: pointer; }
        .drone-tablet button:disabled { background: #3a434c; color: #6c7682; cursor: default; }
        .drone-tablet .blocked { position: absolute; inset: 0; display: grid; place-items: center; background: rgba(10,13,16,0.72); font: 700 18px system-ui, sans-serif; letter-spacing: 0.1em; color: #f0c674; }
      </style>
      <div class="pad">
        <div class="hd"><b>${op.droneKind === 'strike' ? 'STRIKE DRONE' : 'DRONE TASKING'}</b><span class="dr"></span><span class="zoom"><button class="zo" title="zoom out (−)">−</button><button class="zi" title="zoom in (=)">+</button><button class="zr" title="back to the reach (0)">reach</button></span></div>
        <div class="mapwrap"></div>
        <div class="ft"><span class="hint"></span><span class="ft-r"><span class="st"></span> <button class="go">LAUNCH</button></span></div>
      </div>`;
    document.body.appendChild(this.el);
    const wrap = this.el.querySelector('.mapwrap') as HTMLDivElement;
    // the operator stands still with the tablet up: the map can take a bigger slice of each frame to draw
    this.map = new TopoMap(size, { parent: wrap, frame, baseResolution: Math.min(size, 420), budgetMs: 12 });
    this.overlay = document.createElement('canvas');
    this.overlay.className = 'route';
    this.overlay.width = this.overlay.height = size;
    wrap.appendChild(this.overlay);
    this.g = this.overlay.getContext('2d')!;
    this.status = this.el.querySelector('.st') as HTMLElement;
    this.launchBtn = this.el.querySelector('.go') as HTMLButtonElement;
    const hint = this.el.querySelector('.hint') as HTMLElement;
    (this.el.querySelector('.dr') as HTMLElement).textContent = `${op.drones ?? 0} drone${(op.drones ?? 0) === 1 ? '' : 's'}${op.payloadIssue ? ` · ${op.charges ?? 0} charge${(op.charges ?? 0) === 1 ? '' : 's'}` : ''} · reach ${Math.round(droneReach(state) / person)} people`;
    if (opts.blocked) {
      // refused: say why and how to put it away, nothing else (owner)
      const b = document.createElement('div'); b.className = 'blocked'; b.textContent = opts.blocked.toUpperCase(); wrap.appendChild(b);
      hint.textContent = 'esc / G: put away';
      this.launchBtn.style.display = 'none';
    } else hint.innerHTML = `click: waypoint · drag: pan · wheel / − =: zoom · 0: the reach<br>${this.striker ? 's then click: strike (a known enemy or a point) · ' : ''}backspace: undo · enter: launch · esc / G: put away`;

    // input: the tablet's own; the host has idled its capture while the tablet is open
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '`') return;
      const k = e.key.toLowerCase();
      if (k === 'escape' || l0KeyIs('tablet', k)) this.cancel();
      else if (k === 'enter') this.launch();
      else if (k === 'backspace') { if (this.strike || this.arming) { this.strike = null; this.arming = false; } else this.waypoints.pop(); this.draw(); }
      else if (k === 's' && this.striker && !opts.blocked) { this.arming = !this.arming; this.draw(); }
      else if (k === '-' || k === '_') this.zoom(1.4);
      else if (k === '=' || k === '+') this.zoom(1 / 1.4);
      else if (k === '0') { this.map.resetView(); this.draw(); }
      e.preventDefault(); e.stopImmediatePropagation();
    };
    window.addEventListener('keydown', onKey, true);
    this.cleanup.push(() => window.removeEventListener('keydown', onKey, true));
    const at = (e: MouseEvent) => { const r = this.overlay.getBoundingClientRect(); return { x: (e.clientX - r.left) * (size / r.width), y: (e.clientY - r.top) * (size / r.height) }; };
    this.overlay.addEventListener('mousemove', e => {
      const p = at(e);
      if (this.press && (e.buttons & 1)) {
        if (!this.press.dragged && Math.hypot(p.x - this.press.x, p.y - this.press.y) > DRAG_PX) { this.press.dragged = true; this.overlay.classList.add('dragging'); }
        if (this.press.dragged) { this.map.panBy(p.x - this.press.x, p.y - this.press.y); this.press.x = p.x; this.press.y = p.y; }
      }
      this.hover = this.press?.dragged ? null : p; this.draw();
    });
    this.overlay.addEventListener('mouseleave', () => { this.hover = null; this.draw(); });
    this.overlay.addEventListener('mousedown', e => {
      if (e.button === 2) { if (!opts.blocked) { this.waypoints.pop(); this.draw(); } return; }
      if (e.button === 0) { const p = at(e); this.press = { x: p.x, y: p.y, dragged: false }; }
    });
    const onUp = (e: MouseEvent) => {
      if (e.button !== 0 || !this.press) return;
      const wasDrag = this.press.dragged;
      this.press = null; this.overlay.classList.remove('dragging');
      if (!wasDrag && !opts.blocked && this.arming && e.target === this.overlay) { const p = at(e); this.setStrikeAtPixel(p.x, p.y); return; }
      if (!wasDrag && !opts.blocked && this.waypoints.length < MAX_WAYPOINTS && e.target === this.overlay) { const p = at(e); this.addWaypointAtPixel(p.x, p.y); }
    };
    window.addEventListener('mouseup', onUp, true);
    this.cleanup.push(() => window.removeEventListener('mouseup', onUp, true));
    this.overlay.addEventListener('wheel', e => { e.preventDefault(); const p = at(e); this.map.zoomAt(Math.exp(e.deltaY * 0.0015), p.x, p.y); this.draw(); }, { passive: false });
    this.overlay.addEventListener('contextmenu', e => e.preventDefault());
    (this.el.querySelector('.zo') as HTMLButtonElement).addEventListener('click', () => this.zoom(1.4));
    (this.el.querySelector('.zi') as HTMLButtonElement).addEventListener('click', () => this.zoom(1 / 1.4));
    (this.el.querySelector('.zr') as HTMLButtonElement).addEventListener('click', () => { this.map.resetView(); this.draw(); });
    this.launchBtn.addEventListener('click', () => this.launch());
    this.update(state, 0);
  }

  private zoom(factor: number): void { this.map.zoomAt(factor); this.draw(); }

  /** A pixel on the map → a waypoint direction (also used by tests, the sheet and the browser check). */
  addWaypointAtPixel(px: number, py: number): void {
    this.waypoints.push(this.map.dirAtPixel(px, py));
    this.draw();
  }

  /**
   * 65.16: a pixel on the map → the strike: the known enemy drawn nearest it (within a marker's reach on the map — the
   * team's picture, never the truth), else the point under it.
   */
  setStrikeAtPixel(px: number, py: number): void {
    const op = this.state.players.find(p => p.id === this.operatorId);
    let best: { dir: Vec3; unitId: number; d: number } | null = null;
    if (op) for (const b of teamPicture(this.state, op.teamIndex, this.tick)) {
      if (b.unitId === undefined || b.planetIndex !== op.planetIndex) continue;
      const q = this.map.pixelOf(b.surfaceDir);
      const d = Math.hypot(q.x - px, q.y - py);
      if (d <= STRIKE_SNAP_PX && (!best || d < best.d)) best = { dir: b.surfaceDir, unitId: b.unitId, d };
    }
    this.strike = best ? { dir: { ...best.dir }, unitId: best.unitId } : { dir: this.map.dirAtPixel(px, py) };
    this.arming = false;
    this.draw();
  }

  /** The strike set, as the input carries it (65.16), or null. */
  get strikeTarget(): StrikeTarget | null {
    return this.strike ? (this.strike.unitId !== undefined ? { unitId: this.strike.unitId } : { dir: { ...this.strike.dir } }) : null;
  }

  /** Where a direction lands on the map, in the map's pixels (through the current pan and zoom). */
  pixelOf(dir: Vec3): { x: number; y: number } { return this.map.pixelOf(dir); }
  /** The map's size in pixels. */
  get mapSize(): number { return this.size; }

  get route(): Vec3[] { return this.waypoints.slice(); }

  /** Redraw the map's live layer (units move while you plan) and the route. */
  update(state: GameState, tick: number): void {
    this.state = state;
    this.tick = tick;
    const op = state.players.find(p => p.id === this.operatorId);
    this.map.update(state, op?.teamIndex ?? null, this.operatorId, tick);
    this.draw();
  }

  private draw(): void {
    const g = this.g, st = this.state, size = this.size;
    const op = st.players.find(p => p.id === this.operatorId);
    g.clearRect(0, 0, size, size);
    if (!op || !this.map.mapView) return;
    const planet = st.planets[op.planetIndex];
    const person = PERSON_BODY_HEIGHT * personSetOf(st).k;
    const reach = droneReach(st);
    const flash = unitPaletteFor(TEAMS[op.teamIndex % TEAMS.length]).flash;
    const pxPerU = size / (2 * this.map.mapView.extent);
    const route = [op.surfaceDir, ...this.waypoints];
    const out = batteryOut(route, planet.radius, reach);
    const pts = route.map(d => this.map.pixelOf(d));
    // the one-way reach: a ring round the operator
    g.setLineDash([6, 8]); g.lineWidth = 1.5; g.strokeStyle = 'rgba(232,236,240,0.45)';
    g.beginPath(); g.arc(pts[0].x, pts[0].y, reach * pxPerU, 0, Math.PI * 2); g.stroke(); g.setLineDash([]);
    g.font = '600 11px system-ui, sans-serif'; g.fillStyle = 'rgba(232,236,240,0.7)'; g.textAlign = 'center';
    g.fillText('one-way reach', pts[0].x, pts[0].y - reach * pxPerU - 6);
    // legs: solid flash while the battery lasts, dashed red beyond
    g.lineWidth = 3; g.lineCap = 'round';
    for (let i = 0; i + 1 < pts.length; i++) {
      const a = pts[i], b = pts[i + 1];
      const dead = out && i > out.leg;
      if (out && i === out.leg) {
        const c = this.map.pixelOf(out.point);
        seg(g, a, c, flash, false); seg(g, c, b, '#ff5a4a', true);
      } else seg(g, a, b, dead ? '#ff5a4a' : flash, !!dead);
    }
    // the next leg under the cursor, with its length
    if (this.hover && !this.opts.blocked && this.waypoints.length < MAX_WAYPOINTS) {
      const last = pts[pts.length - 1];
      g.setLineDash([4, 5]); g.lineWidth = 1.5; g.strokeStyle = 'rgba(232,236,240,0.7)';
      g.beginPath(); g.moveTo(last.x, last.y); g.lineTo(this.hover.x, this.hover.y); g.stroke(); g.setLineDash([]);
      const leg = routeLength([route[route.length - 1], this.map.dirAtPixel(this.hover.x, this.hover.y)], planet.radius);
      g.font = '600 12px system-ui, sans-serif'; g.fillStyle = '#e8ecf0'; g.textAlign = 'left';
      g.fillText(`${Math.round(leg / person)} people`, this.hover.x + 10, this.hover.y - 8);
    }
    // the operator: where the route starts
    g.beginPath(); g.arc(pts[0].x, pts[0].y, 9, 0, Math.PI * 2); g.lineWidth = 3; g.strokeStyle = '#0b0e12'; g.stroke();
    g.lineWidth = 2; g.strokeStyle = '#ffffff'; g.stroke();
    // waypoints, numbered; the battery-out mark
    pts.slice(1).forEach((p, i) => {
      g.beginPath(); g.arc(p.x, p.y, 9, 0, Math.PI * 2); g.fillStyle = '#0b0e12'; g.fill();
      g.lineWidth = 2; g.strokeStyle = out && (i > out.leg || (i === out.leg)) ? '#ff5a4a' : flash; g.stroke();
      g.font = '700 11px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = '#e8ecf0'; g.fillText(String(i + 1), p.x, p.y + 0.5);
      g.textBaseline = 'alphabetic';
    });
    if (out) {
      const c = this.map.pixelOf(out.point);
      g.strokeStyle = '#ff5a4a'; g.lineWidth = 3;
      g.beginPath(); g.moveTo(c.x - 7, c.y - 7); g.lineTo(c.x + 7, c.y + 7); g.moveTo(c.x + 7, c.y - 7); g.lineTo(c.x - 7, c.y + 7); g.stroke();
    }
    // 65.16: the strike — a red crosshair on its target, dashed from the route's end (it dives from wherever it is in reach)
    if (this.strike) {
      const c = this.map.pixelOf(this.strike.dir), last = pts[pts.length - 1];
      seg(g, last, c, '#ff5a4a', true);
      g.lineWidth = 3; g.strokeStyle = '#ff5a4a';
      g.beginPath(); g.arc(c.x, c.y, 12, 0, Math.PI * 2); g.stroke();
      g.beginPath(); g.moveTo(c.x - 18, c.y); g.lineTo(c.x + 18, c.y); g.moveTo(c.x, c.y - 18); g.lineTo(c.x, c.y + 18); g.stroke();
      g.font = '700 11px system-ui, sans-serif'; g.textAlign = 'center'; g.fillStyle = '#ff5a4a';
      g.fillText(this.strike.unitId !== undefined ? 'STRIKE · UNIT' : 'STRIKE', c.x, c.y - 22);
    }
    if (this.arming && this.hover) {
      g.lineWidth = 2; g.strokeStyle = 'rgba(255,90,74,0.8)';
      g.beginPath(); g.arc(this.hover.x, this.hover.y, 12, 0, Math.PI * 2); g.stroke();
    }
    // status: route against the battery (refused: only why — the overlay says it)
    const len = routeLength(route, planet.radius);
    this.status.textContent = this.opts.blocked ? '' : this.arming ? 'click the strike'
      : this.waypoints.length === 0 ? (this.strike ? 'strike set — it flies straight there' : 'set a course')
      : `route ${Math.round(len / person)} / ${Math.round(reach / person)} people${out ? ' — the battery runs out at ✕' : ''}${this.strike ? ' · strike set' : ''}`;
    this.launchBtn.disabled = !!this.opts.blocked || (this.waypoints.length === 0 && !this.strike);
  }

  launch(): void {
    if (this.closed || this.opts.blocked || (this.waypoints.length === 0 && !this.strike)) return;
    const w = this.waypoints.slice();
    const strike = this.strikeTarget;
    this.close();
    this.opts.onLaunch(w, strike ?? undefined);
  }

  cancel(): void {
    if (this.closed) return;
    this.close();
    this.opts.onCancel();
  }

  private close(): void {
    this.closed = true;
    for (const f of this.cleanup) f();
    this.map.dispose();
    this.el.remove();
  }

  get isOpen(): boolean { return !this.closed; }
}

function seg(g: CanvasRenderingContext2D, a: { x: number; y: number }, b: { x: number; y: number }, color: string, dashed: boolean): void {
  g.setLineDash(dashed ? [6, 6] : []);
  g.strokeStyle = '#0b0e12'; g.lineWidth = 5; g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke();
  g.strokeStyle = color; g.lineWidth = 3; g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke();
  g.setLineDash([]);
}
